import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Card,
  CardContent,
  Grid,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Folder,
  CloudUpload,
  Assessment,
  CheckCircle,
  Cancel,
  Warning,
  Info,
  TrendingUp,
  Schedule,
  AutoAwesome,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { dashboardApi, uploadsApi } from '../../services/benchmarkApi';
import BenchmarkUploadDialog from './BenchmarkUploadDialog';
import BenchmarkAiTransformDialog from './BenchmarkAiTransformDialog';

const RootDiv = styled('div')({
  flexGrow: 1,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const ContentDiv = styled('div')(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(1.5),
}));

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
}));

const StatCard = styled(StyledCard)(({ theme }) => ({
  '& .MuiCardContent-root': {
    padding: theme.spacing(1.5),
  },
}));

const StatValue = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 700,
  color: theme.palette.primary.main,
  marginTop: theme.spacing(0.5),
}));

const StatLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  fontWeight: 500,
}));

const ActivityCard = styled(StyledCard)(({ theme }) => ({
  '& .MuiCardContent-root': {
    padding: theme.spacing(1.5),
  },
}));

const AlertCard = styled(StyledCard)(({ theme }) => ({
  '& .MuiCardContent-root': {
    padding: theme.spacing(1.5),
  },
}));

const QuickActionButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1, 2.5),
  borderRadius: theme.shape.borderRadius,
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
}));

const BenchmarkDashboard = () => {
  const navigate = useNavigate();
  const [uploadType, setUploadType] = useState(null);
  const [aiUploadOpen, setAiUploadOpen] = useState(false);
  const [dashboardStats, setDashboardStats] = useState({
    totalProjects: 0,
    projectsWithBudget: 0,
    projectsWithActuals: 0,
    benchmarkReadyProjects: 0,
  });
  const [recentUploads, setRecentUploads] = useState([]);
  const [uploadsDialogOpen, setUploadsDialogOpen] = useState(false);
  const [error, setError] = useState('');

  const PREVIEW_COUNT = 3;

  const dataQualityAlerts = [];
  if (dashboardStats.totalProjects === 0) {
    dataQualityAlerts.push({
      id: 'no-projects',
      severity: 'info',
      message: 'No projects yet — create a project under Benchmark → Projects',
      count: 1,
    });
  } else if (dashboardStats.projectsWithBudget === 0) {
    dataQualityAlerts.push({
      id: 'no-budget',
      severity: 'warning',
      message: 'Upload Estimate / BoQ files to load estimate data',
      count: dashboardStats.totalProjects,
    });
  }
  if (dashboardStats.totalProjects > 0 && dashboardStats.projectsWithActuals === 0) {
    dataQualityAlerts.push({
      id: 'no-actuals',
      severity: 'info',
      message: 'Upload Actual / Progress files to compare against budget',
      count: dashboardStats.totalProjects,
    });
  }
  if (
    dashboardStats.projectsWithBudget > 0 &&
    dashboardStats.benchmarkReadyProjects < Math.min(2, dashboardStats.totalProjects)
  ) {
    dataQualityAlerts.push({
      id: 'need-peers',
      severity: 'info',
      message: 'Add more projects with cost data to run Project Comparison',
      count: 1,
    });
  }

  const loadDashboard = useCallback(async () => {
    try {
      const [statsRes, uploadsRes] = await Promise.all([
        dashboardApi.stats(),
        uploadsApi.listBatches(),
      ]);
      setDashboardStats({
        totalProjects: statsRes.data?.totalProjects || 0,
        projectsWithBudget: statsRes.data?.projectsWithBudget || 0,
        projectsWithActuals: statsRes.data?.projectsWithActuals || 0,
        benchmarkReadyProjects: statsRes.data?.benchmarkReadyProjects || 0,
      });
      const batches = uploadsRes.data || [];
      setRecentUploads(
        batches.slice(0, 30).map((b) => ({
          id: b.batchId,
          type: b.transactionType,
          projectName: `Project #${b.projectId}`,
          uploadedBy: b.uploadStatus,
          uploadedAt: b.fileName,
          status: b.uploadStatus,
        }))
      );
      setError('');
    } catch (e) {
      setError(e.message || 'Failed to load dashboard');
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const getAlertIcon = (severity) => {
    switch (severity) {
      case 'error':
        return <Warning color="error" />;
      case 'warning':
        return <Warning color="warning" />;
      default:
        return <Info color="info" />;
    }
  };

  const getAlertColor = (severity) => {
    switch (severity) {
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      default:
        return 'info';
    }
  };

  /** Status icon for Recent Uploads — do not always show green success. */
  const getUploadStatusIcon = (status) => {
    const s = String(status || '').toUpperCase();
    if (s.includes('FAIL') || s.includes('ERROR')) {
      return <Cancel sx={{ color: 'error.main', fontSize: 18 }} titleAccess={status} />;
    }
    if (s.includes('PARTIAL') || s.includes('WARN')) {
      return <Warning sx={{ color: 'warning.main', fontSize: 18 }} titleAccess={status} />;
    }
    if (s.includes('SUCCESS') || s.includes('COMPLETE') || s.includes('OK')) {
      return <CheckCircle sx={{ color: 'success.main', fontSize: 18 }} titleAccess={status} />;
    }
    return <Info sx={{ color: 'info.main', fontSize: 18 }} titleAccess={status || 'unknown'} />;
  };

  const renderUploadItem = (upload, index, total) => (
    <React.Fragment key={upload.id}>
      <ListItem sx={{ px: 0, py: 1 }}>
        <ListItemIcon sx={{ minWidth: 36 }}>
          <CloudUpload color="primary" sx={{ fontSize: '1.1rem' }} />
        </ListItemIcon>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {upload.projectName}
              </Typography>
              <Chip
                label={upload.type}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: '20px', fontSize: '0.7rem' }}
              />
            </Box>
          }
          secondary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                {upload.uploadedBy}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                • {upload.uploadedAt}
              </Typography>
            </Box>
          }
        />
        {getUploadStatusIcon(upload.status)}
      </ListItem>
      {index < total - 1 && <Divider />}
    </React.Fragment>
  );

  const previewUploads = recentUploads.slice(0, PREVIEW_COUNT);

  return (
    <RootDiv>
      <ContentDiv>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
            Benchmark Hub
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Overview and metrics for benchmark analysis
          </Typography>
        </Box>

        {/* Project Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Folder sx={{ color: 'primary.main', mr: 0.75, fontSize: '1.2rem' }} />
                  <StatLabel>Total Projects</StatLabel>
                </Box>
                <StatValue>{dashboardStats.totalProjects}</StatValue>
              </CardContent>
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Assessment sx={{ color: 'primary.main', mr: 0.75, fontSize: '1.2rem' }} />
                  <StatLabel>Projects with Estimate / BoQ Loaded</StatLabel>
                </Box>
                <StatValue>{dashboardStats.projectsWithBudget}</StatValue>
              </CardContent>
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <TrendingUp sx={{ color: 'primary.main', mr: 0.75, fontSize: '1.2rem' }} />
                  <StatLabel>Projects with Actuals Loaded</StatLabel>
                </Box>
                <StatValue>{dashboardStats.projectsWithActuals}</StatValue>
              </CardContent>
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircle sx={{ color: 'success.main', mr: 1 }} />
                  <StatLabel>Benchmark-Ready Projects</StatLabel>
                </Box>
                <StatValue>{dashboardStats.benchmarkReadyProjects}</StatValue>
              </CardContent>
            </StatCard>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          {/* Recent Uploads Activity Feed */}
          <Grid item xs={12} md={6}>
            <ActivityCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <Schedule sx={{ color: 'primary.main', mr: 0.75, fontSize: '1.1rem' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Recent Uploads Activity
                  </Typography>
                </Box>
                <List sx={{ p: 0 }}>
                  {previewUploads.map((upload, index) =>
                    renderUploadItem(upload, index, previewUploads.length)
                  )}
                  {recentUploads.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                      No uploads yet.
                    </Typography>
                  )}
                </List>
                {recentUploads.length > PREVIEW_COUNT && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                    <Button size="small" onClick={() => setUploadsDialogOpen(true)}>
                      Show more ({recentUploads.length - PREVIEW_COUNT} more)
                    </Button>
                  </Box>
                )}
              </CardContent>
            </ActivityCard>
          </Grid>

          {/* Data Quality Alerts */}
          <Grid item xs={12} md={6}>
            <AlertCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Warning sx={{ color: 'warning.main', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Data Quality Alerts
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {dataQualityAlerts.length === 0 && (
                    <Alert severity="success" icon={<CheckCircle color="success" />}>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        No quality alerts — projects and uploads look ready for comparison.
                      </Typography>
                    </Alert>
                  )}
                  {dataQualityAlerts.map((alert) => (
                    <Alert
                      key={alert.id}
                      severity={getAlertColor(alert.severity)}
                      icon={getAlertIcon(alert.severity)}
                      sx={{ alignItems: 'center' }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{alert.message}</Typography>
                        <Chip
                          label={alert.count}
                          size="small"
                          color={getAlertColor(alert.severity)}
                          sx={{ ml: 1, height: '20px', fontSize: '0.7rem' }}
                        />
                      </Box>
                    </Alert>
                  ))}
                </Box>
              </CardContent>
            </AlertCard>
          </Grid>

          {/* Quick Actions */}
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 2,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                Quick Actions
              </Typography>
              {error && (
                <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <QuickActionButton
                  variant="contained"
                  color="primary"
                  startIcon={<CloudUpload sx={{ fontSize: '1rem' }} />}
                  onClick={() => setUploadType('estimate')}
                  size="medium"
                >
                  Upload Estimate / BoQ
                </QuickActionButton>
                <QuickActionButton
                  variant="outlined"
                  color="primary"
                  startIcon={<AutoAwesome sx={{ fontSize: '1rem' }} />}
                  onClick={() => setAiUploadOpen(true)}
                  size="medium"
                >
                  Upload with AI
                </QuickActionButton>
                <QuickActionButton
                  variant="contained"
                  color="primary"
                  startIcon={<CloudUpload sx={{ fontSize: '1rem' }} />}
                  onClick={() => setUploadType('change-order')}
                  size="medium"
                >
                  Upload Change Order
                </QuickActionButton>
                <QuickActionButton
                  variant="contained"
                  color="primary"
                  startIcon={<CloudUpload sx={{ fontSize: '1rem' }} />}
                  onClick={() => setUploadType('actual')}
                  size="medium"
                >
                  Upload Actual Cost
                </QuickActionButton>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </ContentDiv>

      <BenchmarkUploadDialog
        open={Boolean(uploadType)}
        uploadType={uploadType || 'estimate'}
        onClose={() => setUploadType(null)}
        onOpenAiUpload={() => {
          setUploadType(null);
          setAiUploadOpen(true);
        }}
        onSuccess={() => {
          loadDashboard();
        }}
      />

      <BenchmarkAiTransformDialog
        open={aiUploadOpen}
        onClose={() => setAiUploadOpen(false)}
        onSuccess={() => {
          setAiUploadOpen(false);
          loadDashboard();
        }}
      />

      <Dialog
        open={uploadsDialogOpen}
        onClose={() => setUploadsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Recent Uploads Activity</DialogTitle>
        <DialogContent dividers sx={{ maxHeight: 420 }}>
          <List sx={{ p: 0 }}>
            {recentUploads.map((upload, index) =>
              renderUploadItem(upload, index, recentUploads.length)
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </RootDiv>
  );
};

export default BenchmarkDashboard;
